import React, { useCallback, useReducer, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useMutation } from '@apollo/client'

import { BackContainer } from 'components/Common/BackContainer'
import BodyContainerWithHeader from 'components/Common/BodyContainerWithHeader'

import { DUPLICATE_KEY_ERROR } from 'constants/error'
import { CAR_SETTINGS_SUB_ROUTES } from 'constants/routes'
import { textFiles } from 'constants/textFiles'
import useNotification from 'hooks/useNotification'
import useTranslation from 'hooks/useTranslation'
import { DriveTrain } from 'models/car'
import {
  DriveTrainCreationAction,
  DriveTrainCreationModel,
} from 'models/driveTrainCreation'
import {
  BaseIdEntity,
  GenericData,
  GenericInputVariable,
} from 'models/services/base'
import { CreateCarSettingGenericInputType } from 'models/services/CarSettings/creation'
import { PublicationStatus } from 'models/status'
import driveTrainCreationReducer from 'reducers/driveTrainCreationReducer'
import {
  createDriveTrainStepperItems,
  INITIAL_DRIVE_TRAIN_CREATION_STEPS as steps,
} from 'utils/CarSettings/DriveTrain/creation'
import { validateGraphQLErrorCode } from 'utils/error'
import { getTranslationsFilteredByLanguage } from 'utils/translation'

import { CREATE_DRIVE_TRAIN } from 'graphQL/Operations/DriveTrain/Creation/mutations'

import { StyledBox } from 'styles/operation/creation'

const initialDriveTrainInformationState: DriveTrain = {
  name: '',
  status: PublicationStatus.PUBLISHED,
  translations: [],
}

const initialData: DriveTrainCreationModel = {
  driveTrainInformation: initialDriveTrainInformationState,
}

const DriveTrainCreationPage = () => {
  const [currentStep, setCurrentStep] = useState<number>(0)

  const [driveTrainData, dispatch] = useReducer(
    driveTrainCreationReducer,
    initialData
  )

  const { driveTrainInformation } = driveTrainData
  const { translations } = driveTrainInformation

  const history = useHistory()
  const { show } = useNotification()

  const { text } = useTranslation(textFiles.DRIVE_TRAIN_CREATION)
  const { text: generalText } = useTranslation(textFiles.GENERAL)
  const { stepper } = text

  const stepperItems = createDriveTrainStepperItems(stepper)

  const updateDriveTrainData = (action: DriveTrainCreationAction) => {
    dispatch(action)
  }

  const handleBack = () => {
    if (currentStep - 1 >= 0) setCurrentStep((step) => step - 1)
  }
  const handleContinue = () => {
    setCurrentStep((step) => step + 1)
  }

  const [createDriveTrain, { loading: submitLoading }] = useMutation<
    GenericData<BaseIdEntity>,
    GenericInputVariable<CreateCarSettingGenericInputType>
  >(CREATE_DRIVE_TRAIN, {
    onCompleted() {
      show({
        updatedSeverity: 'success',
      })
      history.push(CAR_SETTINGS_SUB_ROUTES.DRIVE_TRAIN_LISTING)
    },
    onError(error) {
      const { errorExists } = validateGraphQLErrorCode(
        error,
        DUPLICATE_KEY_ERROR
      )
      if (errorExists) {
        show({
          updatedSeverity: 'error',
          message: generalText.notificationText.duplicateName,
        })
      } else
        show({
          updatedSeverity: 'error',
        })
    },
  })

  const handleSubmit = useCallback(() => {
    const { valueEn, valueEs } = getTranslationsFilteredByLanguage(translations)

    createDriveTrain({
      variables: {
        input: {
          name: driveTrainInformation.name,
          name_translations: {
            en: valueEn ? valueEn.description : null,
            es: valueEs ? valueEs.description : null,
          },
          status: driveTrainInformation.status
            ? PublicationStatus.PUBLISHED
            : PublicationStatus.UNPUBLISHED,
        },
      },
    })
  }, [driveTrainInformation, createDriveTrain, translations])

  return (
    <StyledBox>
      <BackContainer />
      <BodyContainerWithHeader
        title={text.title}
        subtitle={text.processDescription}
        stepperItems={stepperItems}
        currentStep={currentStep}
      >
        {React.createElement(steps[currentStep], {
          handleContinue,
          handleBack,
          handleSubmit,
          submitLoading,
          driveTrainData,
          updateDriveTrainData,
        })}
      </BodyContainerWithHeader>
    </StyledBox>
  )
}

export default DriveTrainCreationPage
